import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useAppSelector } from 'src/store';
import { RootState } from 'src/store';
import { useShortLinkApi } from '../api/ShortLinksApi';
import { resetError, setLoading, setList, setError, setFilter, resetList } from '../slices';
import { CreateShortLinkRequest, UpdateShortLinkRequest } from '../types';

const getServerSortField = (sort?: string): string | undefined => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    clickCount: 'click_count',
    createdBy: 'created_by',
    expiresAt: 'expires_at',
    createdAt: 'date_created',
    lastClick: 'last_click',
  };

  const [field, order] = sort.split('_');
  const serverField = sortMap[field] || field;

  return `${serverField}_${order}`;
};

export const useShortLinkService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector(state => state.shortLink);

  const api = useShortLinkApi();

  const getShortLinks = useCallback(async (companyId: string) => {
    const filter = getState().shortLink.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getShortLinks({
        companyId,
        limit: filter.limit,
        offset: filter.offset,
        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeShortLinkFilter = useCallback((filter: Partial<RootState['shortLink']['filter']>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetShortLink = useCallback(() => {
    dispatch(resetList());
  }, []);

  const createShortLink = useCallback(async (companyId: string, data: CreateShortLinkRequest) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ create: true }));

      const response = await api.createShortLink(companyId, data);
      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ create: false }));
    }
  }, []);

  const updateShortLink = useCallback(
    async (companyId: string, linkId: string, data: UpdateShortLinkRequest) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ update: true }));

        await api.updateShortLink(companyId, linkId, data);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ update: false }));
      }
    },
    [],
  );

  const deleteShortLink = useCallback(async (companyId: string, linkId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ delete: true }));

      await api.deleteShortLink(companyId, linkId);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ delete: false }));
    }
  }, []);

  const toggleShortLinkStatus = useCallback(
    async (
      companyId: string,
      linkId: string,
      url: string,
      isActive: boolean,
      expiresAt: string | null,
    ) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ update: true }));

        await api.updateShortLink(companyId, linkId, {
          url,
          isActive,
          expiresAt: expiresAt || null,
        });
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ update: false }));
      }
    },
    [],
  );

  return {
    loading,
    filter,
    list,
    error,
    getShortLinks,
    changeShortLinkFilter,
    resetShortLink,
    createShortLink,
    updateShortLink,
    deleteShortLink,
    toggleShortLinkStatus,
  };
};
